import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button, Col, Row, Tooltip } from 'antd';
import PortraitPlaceholder from '@/pages/Header/PortraitPlaceholder.png';
import { HeartFilled, HeartOutlined } from '@ant-design/icons';

import MaterialButton from '../MaterialButton';
import RepeatDisclaimer from '../RepeatDisclaimer';
import TypeBadge from '../TypeBadge';

import { useLocalFileByUUID } from '@shared/hooks/localFiles';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import css from './styles.module.scss';

import { TOPIC_TYPES } from '@/constants/topic-types';
import comments from '@/assets/images/comments.svg';
import lock from '@/assets/images/lock.svg';
import locked from '@/assets/images/locked.svg';
import statistic from '@/assets/images/material-statistic-plan.svg';
import repost from '@/assets/images/repost.svg';
import views from '@/assets/images/views.svg';

const MYRECOMMENDED = 'myRecommended';
const RECOMMENDED = 'recommended';
const MYMATERIAL = 'myMaterial';

const ListItem = ({
  typeList,
  recUsers,
  user,
  onFavoriteClick,
  topic,
  onDeleteClick,
  isExternalLogin,
  isAnother,
  lastRec,
  theLast,
}) => {
  const { t } = useTranslation(['listItem', 'constants']);

  const currentLocalPhotoUUID =
    typeList === MYRECOMMENDED || typeList === MYMATERIAL
      ? user.localPhotoUuid
      : recUsers && recUsers[0].localPhotoUuid;

  const handleFavoriteClick = () => {
    onFavoriteClick(topic.id, typeList);
  };

  const handleDeleteClick = () => {
    onDeleteClick(topic.id, typeList);
  };

  const topicType = topic.type && TOPIC_TYPES(t).find(type => type.id === topic.type.id);
  const recUsersArr = lastRec && lastRec.length > 0 ? lastRec : recUsers;
  const typeBadge = topicType ? (
    <TypeBadge type={topicType.code} name={topic.type.name} className={css.markerOnly} noText />
  ) : null;
  const bigName = topic.name.length > 100;
  const cutTopicName = bigName ? (
    <Tooltip title={topic.name} placement={theLast ? 'top' : 'bottom'}>
      <span>
        <Link
          data-external-login={!isExternalLogin && topic.scope === 'INTERNAL'}
          to={`/collection-topics/${topic.id}`}
          className={css.material__list__title}
        >
          {typeBadge}
          {!isExternalLogin && topic.scope === 'INTERNAL' && (
            <img className={css['material-list__item__locked']} src={locked} alt='' />
          )}
          <span className={css['material-list__item__name']}>{`${topic.name.substr(0, 100)}...`}</span>
        </Link>
      </span>
    </Tooltip>
  ) : (
    <span>
      <Link
        data-external-login={!isExternalLogin && topic.scope === 'INTERNAL'}
        to={`/collection-topics/${topic.id}`}
        className={css.material__list__title}
      >
        {typeBadge}
        {!isExternalLogin && topic.scope === 'INTERNAL' && (
          <img className={css['material-list__item__locked']} src={locked} alt='' />
        )}
        <span>{topic.name}</span>
      </Link>
    </span>
  );
  const toMyCollectionButton =
    (!isAnother ? typeList !== MYRECOMMENDED : typeList) &&
    (topic.isFavourite ? (
      <Button
        className={classNames(
          css['App-btn'],
          css['App-favorite'],
          topic.scope === 'INTERNAL' && !isExternalLogin && css['externalLogin']
        )}
        type='link'
        onClick={handleDeleteClick}
        icon={<HeartFilled className={css['App-favorite-icon']} />}
        size='large'
      ></Button>
    ) : (
      <Button
        className={classNames(css['App-btn'], topic.scope === 'INTERNAL' && !isExternalLogin && css['externalLogin'])}
        onClick={handleFavoriteClick}
        type='link'
        icon={<HeartOutlined className={css['App-favorite-icon']} />}
        size='large'
      ></Button>
    ));

  const DELETED_USER =
    typeList !== RECOMMENDED
      ? user?.status === 'DELETED'
      : recUsersArr[0] && recUsersArr[0].status && recUsersArr[0].status === 'DELETED';

  const [currentAvatar] = useLocalFileByUUID(currentLocalPhotoUUID, { defaultImg: PortraitPlaceholder });

  return (
    <Row
      className={classNames(
        css['material-list__item'],
        typeList === 'myMaterial' && css['material-list__item__myrecomended']
      )}
    >
      {typeList === RECOMMENDED && (
        <Col md={7} lg={5}>
          <Link target='_blank' to={`/collegues/${recUsers[0].id}`} className={css['material-list__item__user']}>
            <span
              className={classNames(css['material-list__item__avatar'], {
                [css.DeleteUser]: DELETED_USER,
              })}
            >
              <img
                className={classNames({
                  [css['DeleteUser-image']]: DELETED_USER,
                })}
                src={currentAvatar}
                alt=''
              />
            </span>
            {DELETED_USER && (
              <Tooltip title={t('userDeleted')}>
                <div className={classNames(css['DeleteUser-key'], css['DeleteUser-key-fix1'])}>
                  <img key='deleteUser' src={lock} alt='' />
                </div>
              </Tooltip>
            )}
            <div>
              {`${recUsersArr[0].firstName} ${recUsersArr[0].lastName.charAt(0)}.`}
              {recUsers.length > 1 && (
                <div className={css['material-list__item__user__andMore']}>и еще {recUsers.length - 1}</div>
              )}
            </div>
          </Link>
        </Col>
      )}
      {typeList === MYRECOMMENDED && (
        <Col md={5} lg={4}>
          <Link target='_blank' to={`/collegues/${user.id}`} className={css['material-list__item__user']}>
            <img src={repost} className={css['material-list__item__repost']} alt='' />
            <span
              className={classNames(css['material-list__item__avatar'], {
                [css.DeleteUser]: DELETED_USER,
              })}
            >
              <img
                className={classNames({
                  [css['DeleteUser-image']]: DELETED_USER,
                })}
                src={currentAvatar}
                alt=''
              />
            </span>
            {DELETED_USER && (
              <Tooltip title={t('userDeleted')}>
                <div className={classNames(css['DeleteUser-key'], css['DeleteUser-key-fix2'])}>
                  <img key='deleteUser' src={lock} alt='' />
                </div>
              </Tooltip>
            )}
            <div>
              {`${user.firstName} ${user.lastName.charAt(0)}.`}
              {recUsers.length > 1 && typeList.includes(RECOMMENDED) && (
                <div className={css['material-list__item__user__andMore']}>и еще {recUsers.length - 1}</div>
              )}
            </div>
          </Link>
        </Col>
      )}
      <Col md={typeList !== MYMATERIAL ? 10 : 12} lg={typeList !== MYMATERIAL ? 13 : 15}>
        <div className={css['material-list__item__name']}>
          <div data-viewed={!isAnother && topic.viewers && !topic.viewers.viewed && typeList === RECOMMENDED}>
            {!isExternalLogin && topic.scope !== 'INTERNAL' ? (
              cutTopicName
            ) : (
              <Tooltip title={t('blocked')} placement={theLast && bigName ? 'bottom' : 'top'}>
                {cutTopicName}
              </Tooltip>
            )}
          </div>
        </div>
      </Col>

      {typeList === MYMATERIAL ? (
        <Col xs={0} sm={0} md={3}>
          <div className={css['material-list__item__feedback__box']}>
            <Tooltip placement='bottom' title={t('viewed')}>
              <div className={css['material-list__item__feedback']}>
                <img src={views} alt='' /> {topic.viewers.count}
              </div>
            </Tooltip>
            <Tooltip placement='bottom' title={t('feedback')}>
              <div className={css['material-list__item__feedback']}>
                <img src={comments} alt='' /> {topic.mention ? topic.mention.count : 0}
              </div>
            </Tooltip>
            <Tooltip placement='bottom' title={t('userPlanned')}>
              <div className={css['material-list__item__feedback']}>
                <img src={statistic} alt='' /> {topic.planStatus ? topic.planStatus.count : 0}
              </div>
            </Tooltip>
          </div>
        </Col>
      ) : (
        <Col md={8} lg={6}>
          {!isExternalLogin && topic.scope !== 'INTERNAL' ? (
            <MaterialButton topic={topic} inline />
          ) : (
            toMyCollectionButton
          )}
        </Col>
      )}
      <RepeatDisclaimer />
    </Row>
  );
};

ListItem.propTypes = {
  isExternalLogin: PropTypes.any,
  isAnother: PropTypes.bool,
  lastRec: PropTypes.any,
  onDeleteClick: PropTypes.func,
  onFavoriteClick: PropTypes.func,
  recUsers: PropTypes.array,
  theLast: PropTypes.bool,
  topic: PropTypes.object,
  typeList: PropTypes.string,
  user: PropTypes.object,
};

export default ListItem;
